/**
 * Entity Configuration
 *
 * Centralized configuration for every entity the Texture platform models —
 * core product objects (app, site, device…), physical grid-infrastructure
 * elements (transformer, recloser, pole…), and behind-the-meter connected
 * devices (battery, EV charger, thermostat…).
 *
 * Each entity carries its display label, a Phosphor icon, an optional
 * description, and a `category` so surfaces can group them (Platform / Grid
 * element / Device).
 *
 * Use this for consistent entity representation across the application:
 * - Navigation
 * - Page headers
 * - Breadcrumbs
 * - Empty states
 * - Related entity links
 *
 * @example
 * ```tsx
 * import { ENTITY_CONFIG } from "@texturehq/edges/config/entities";
 *
 * const siteConfig = ENTITY_CONFIG.site;
 * // { icon: "MapPin", category: "platform", label: { singular: "Site", plural: "Sites" }, ... }
 * ```
 */

import type { IconName } from "../components/Icon";
import type { FieldFormat } from "../utils/formatting/types";

/** Grouping for an entity. Lets surfaces section grid elements and devices
 *  apart from core platform objects. */
export type EntityCategory = "platform" | "grid" | "device";

/**
 * The base page composition an entity renders with. Thirty in-scope entities
 * share these five compositions — the per-entity variation is which blocks
 * appear (driven by this config), not per-entity page code.
 *
 * - `device`         — Battery, EV Charger, Inverter, Thermostat, EV, Water Heater.
 *                      Leads with `entity.now` (live state + headline metrics).
 * - `platform`       — Site, Customer. No `Now` block.
 * - `grid-energized` — energized grid equipment (Transformer, Recloser, Meter…).
 * - `grid-line`      — lines & structures (overhead/underground spans, support structures).
 *
 * Matches the design's coverage matrix (DESIGN-REFERENCE.md §2). Every entry in
 * `ENTITY_CONFIG` carries an archetype, even the 13 that have no detail page —
 * the archetype is the composition it *would* use; `detailPage` gates whether a
 * page is actually reachable.
 */
export type EntityArchetype = "device" | "platform" | "grid-energized" | "grid-line";

/**
 * A serializable `FieldFormat`. `entity.now` and every block that consumes a
 * config must be JSON-serializable (the core block contract forbids functions
 * in props), so the function-carrying `custom` format variant is excluded here
 * at the type level — a metric can never smuggle a `formatter` into the registry.
 */
export type SerializableFieldFormat = Exclude<FieldFormat, { type: "custom" }>;

/**
 * Where a headline metric's value comes from — its provenance. A pure-data
 * discriminated union (no functions), so the whole registry stays
 * JSON-serializable. Added in MR-07: MR-06's `HeadlineMetric` could only express
 * a dot-path into a resolved *device* snapshot; grid metrics are either static
 * nameplate (a JSONB `elementData` key) or a derived telemetry rollup, and
 * authoring those against a bare `path` would misrepresent how they are fetched.
 *
 * - `state`    — device snapshot, resolved from a dot-path (the MR-06 shape).
 *                e.g. `state.gridPower`. This is what every device/meter row uses.
 * - `metadata` — a static nameplate value read from `GridElement.elementData`
 *                (JSONB). The `key` is the JSONB key, *not* a dot-path.
 * - `derived`  — an asynchronous telemetry rollup with hours-to-a-day latency
 *                (e.g. transformer loading: SUM of downstream meter `kwh_usage`
 *                ÷ PF ÷ `ratedKva`, a 0–1 ratio the cell scales ×100). Consumers
 *                MUST treat this as async/loading and never block the card on it.
 * - `scada`    — a live SCADA telemetry reading resolved from the element's
 *                `TelemetrySource` (`externalId = '{'+elementGuid+'}'`). `points`
 *                are the TelemetryPoint names to read, `reduce` collapses them
 *                (`maxAbs` for three-phase current), `unit` is passed through,
 *                and `sla` (ms) is the freshness window past which the reading
 *                reads "as of {rel}", not live. Optional `ignore` lists numeric
 *                sentinel values (e.g. `32767` on a tap-position phase) that are
 *                treated as absent — skipped before the reduce and before the
 *                freshness check, exactly as a `null` reading would be. Added in
 *                MR-EN4; paired with the grid capability resolver. The gate is a
 *                fresh point, not `ScadaGridNode` existence; a stale point never
 *                blocks the card.
 *
 * No grid row uses `metadata`/`derived`/`scada` yet — the design's grid metric proposal
 * is still "For review" (see MR-07). The type is extended now so those rows can
 * be authored honestly once design signs off; only `state` rows exist today.
 */
export type MetricSource =
  | { kind: "state"; path: string }
  | { kind: "metadata"; key: string }
  | { kind: "derived"; rollup: "transformer-loading"; window: "15m" | "1h" | "1d" }
  | {
      kind: "scada";
      points: string[];
      reduce: "maxAbs" | "sum" | "first";
      unit: string;
      sla: number;
      ignore?: number[];
    };

/**
 * One headline metric shown by `entity.now`. A serializable declaration only —
 * `source` says where the value comes from (see `MetricSource`), `label` is a
 * translation key, `format` says how to render the value, and `icon` is a
 * registry key (never a component). No functions: the host resolves the source
 * and format at render.
 *
 * The shape is the serializable subset of `device-config`'s `FieldConfig`
 * (`id`/`label`/`source`/`format`/`icon`) — `showWhen`/`resolver`/`custom`
 * formatters are intentionally dropped. MR-07 replaced the bare `path` with a
 * `source` discriminator; a `state` source carries the former dot-path as
 * `source.path`, so no device row changed meaning.
 */
export interface HeadlineMetric {
  /** Stable identifier for the metric (unique within an entity). */
  id: string;
  /** Translation key for the display label. */
  label: string;
  /** Where the value comes from (device snapshot, nameplate, or rollup). */
  source: MetricSource;
  /** How to render the value. Serializable formats only. */
  format: SerializableFieldFormat;
  /** Optional icon registry key. */
  icon?: IconName;
}

/**
 * A serializable rule for deriving a display state from resolved entity data.
 * Mirrors `device-config`'s `StateRule` but drops the optional `resolver`
 * function so the registry stays JSON-serializable. `mapping` maps a raw field
 * value to a display-state token; `fallback` is used when the field is
 * missing/unmapped.
 */
export interface EntityStateRule {
  /** Dot-path to the source field in the resolved entity data. */
  fieldPath: string;
  /** Raw-value → display-state-token map. */
  mapping: Record<string, string>;
  /** State token to use when the field is missing or unmapped. */
  fallback: string;
  /** Optional translation key for the state's display label. */
  label?: string;
}

/**
 * The render format for a static grid property. This is the serializable subset
 * of `@texturehq/grid-element-config`'s `MetadataFieldConfig["format"]` — the
 * same shape, kept structurally identical so the two stay in sync when stat-list
 * rows are transcribed. Deliberately *not* `SerializableFieldFormat`: nameplate
 * ratings carry units the edges `FieldFormat` unit enums don't admit (e.g. a
 * transformer's `kVA`, a capacitor's `kVar`), so `unit` is a free string here.
 */
export interface GridStatFieldFormat {
  type: "voltage" | "power" | "distance" | "text" | "number";
  unit?: string;
  decimals?: number;
}

/**
 * One row in a grid entity's static-properties (`data.stat-list`) block. A
 * serializable declaration transcribed from `grid-element-config.metadataFields`
 * (MR-07 step 2): `key` is the `GridElement.elementData` JSONB key, `label` is
 * the display label, `format` says how to render it. The source config's
 * `showWhen` predicate is intentionally dropped — a stat-list row is data only,
 * and per-instance visibility is a render-time concern, not registry state.
 *
 * This is nameplate, not "now": static properties (kVA rating, conductor type,
 * meter number, length) belong here, never in `fields.headlineMetrics`, which is
 * the live block. See MR-07 "'has data' ≠ 'belongs in entity.now'".
 */
export interface GridStatField {
  /**
   * Key in the element's resolved data. Usually a `GridElement.elementData`
   * JSONB key; may also be a decoded top-level field the host merges in (e.g.
   * `capacityKva`, the decoded transformer kVA rating).
   */
  key: string;
  /** Display label (translation key or plain text). */
  label: string;
  /** How to render the value. Optional — plain text when omitted. */
  format?: GridStatFieldFormat;
}

export interface EntityConfig {
  /** Phosphor or Texture custom icon name. The canonical (resting / default) glyph. For switchable
   *  elements this mirrors `states.closed.icon` (or the base glyph when the
   *  state only changes color). */
  icon: IconName;
  /** Grouping for the entity (platform object, grid element, or device). */
  category: EntityCategory;
  /** Display labels */
  label: {
    singular: string;
    plural: string;
  };
  /** Optional description */
  description?: string;
  /**
   * Optional open/closed state variants for switchable elements. State is
   * expressed one of two ways, per the identity-vs-state boundary:
   *   • GEOMETRY — the shape moves (Switch, Sectionalizer): set `icon` per state.
   *   • COLOR    — the shape is fixed, the tint changes (Breaker, Recloser):
   *                set `color` per state; the glyph stays `EntityConfig.icon`.
   * A state may set both. When `icon` is omitted the entity's base `icon` is
   * used and only the color differs. Color is applied at the RENDER layer via
   * the documented `GRID_STATE_COLORS` token — never baked into the glyph SVG.
   */
  states?: {
    open: EntityStateVariant;
    closed: EntityStateVariant;
  };

  // ──────────────────────────────────────────────────────────────────────
  // Metric layer (MR-06). Added on top of the display metadata above so that
  // `entity.now`, the archetype resolver and `entity.header` derive everything
  // from this one registry — "no per-instance metric props" (design build spec).
  // ──────────────────────────────────────────────────────────────────────

  /**
   * The base page composition this entity renders with. Required on all 43
   * entries — the archetype is the composition an entity *would* use even when
   * `detailPage` is false.
   */
  archetype: EntityArchetype;
  /**
   * Whether this entity has a reachable detail page. 30 of 43 are `true`; the
   * registry is the single answer to "does this entity have a page?".
   */
  detailPage: boolean;
  /**
   * `entity.now` field config. Present only for entities that show a `Now`
   * block (devices with a `device-config` source, plus Meter). Absent means
   * `entity.now` declines to render — grid elements omit this until the design's
   * (currently "For review") metric proposal is approved, and Site/Customer
   * omit it because their composition has no `Now` block.
   */
  fields?: {
    /**
     * The 2–4 headline metrics `entity.now` shows. The cell count adapts across
     * that range; the bound is enforced by the conformance test, not just docs.
     */
    headlineMetrics: HeadlineMetric[];
  };
  /**
   * Which headline metric `entity.now` promotes to the hero ("fused") slot — a
   * single large number the design leads the rail with (e.g. Battery → SoC,
   * Inverter → Output). MUST be the `id` of one of this entity's
   * `fields.headlineMetrics`; the conformance test enforces that. Optional:
   * entities with no metric rail (Gateway, Water Heater) omit it, and a rail
   * with no promoted hero simply renders every cell equally. The hero choice
   * lives here, alongside the serialized metrics, so no per-instance prop is
   * needed (design build spec: "no per-instance metric props").
   */
  fusedMetricId?: string;
  /**
   * State rules for `entity.now`'s status chips. `primaryState` drives the
   * pulsing primary chip; `secondaryState` renders a second chip with no pulse.
   *
   * Only `battery` declares a `secondaryState` today (charge + grid). This
   * docstring used to say "battery/inverter" — `inverter` has never had one, and
   * the fix is this comment, not the code: adding a `secondaryState` to inverter
   * would put a new chip on a live page, which is a product change wearing a
   * typo's clothes.
   */
  stateRules?: {
    primaryState: EntityStateRule;
    secondaryState?: EntityStateRule;
  };
  /**
   * Static-properties block config (MR-07 step 2). Feeds the `data.stat-list`
   * block — nameplate/GIS attributes that are *properties*, not *now*. Present
   * on grid entities (transcribed from `grid-element-config.metadataFields` via
   * `GRID_ELEMENT_TYPE_BY_ENTITY`); absent on devices and platform objects,
   * which surface their data through other blocks. This is the genuinely
   * unblocked, data-backed grid path — distinct from the design-gated live
   * `fields`/`stateRules` above.
   */
  properties?: {
    /** The static property rows shown by the `data.stat-list` block. */
    statList: GridStatField[];
  };
}

/** One open/closed state's presentation: an optional glyph override and/or a
 *  status color. Omitting `icon` means "use the entity's base icon, recolored". */
interface EntityStateVariant {
  icon?: IconName;
  color?: GridStateColor;
}

/**
 * Status palette for grid-element states. This is the SINGLE source for both
 * the live render-layer tint (Breaker/Recloser recolor their glyph by state)
 * and the docs/QA legend. `className` is the Edges Tailwind token utility
 * applied to the icon wrapper (glyph inherits via `currentColor`); `token` is
 * the raw CSS variable for non-Tailwind consumers. The glyph SVG itself stays
 * monochrome — color lives here, at the render layer, not in the icon.
 */
export const GRID_STATE_COLORS = {
  open: {
    className: "text-feedback-success-border",
    token: "--color-feedback-success-border",
    label: "Open",
  },
  closed: {
    className: "text-feedback-error-border",
    token: "--color-feedback-error-border",
    label: "Closed",
  },
} as const;

export type GridStateColor = keyof typeof GRID_STATE_COLORS;

/** Display metadata for each category — label, blurb, icon, and sort order. */
export interface EntityCategoryConfig {
  label: string;
  description: string;
  icon: IconName;
  order: number;
}

export const ENTITY_CATEGORY_CONFIG = {
  platform: {
    label: "Platform",
    description: "Core product objects that structure the Texture workspace.",
    icon: "Stack",
    order: 0,
  },
  grid: {
    label: "Grid Elements",
    description: "Physical utility-grid infrastructure imported from a customer's GIS export.",
    icon: "TreeStructure",
    order: 1,
  },
  device: {
    label: "Devices",
    description: "Behind-the-meter connected energy devices.",
    icon: "Lightning",
    order: 2,
  },
} as const satisfies Record<EntityCategory, EntityCategoryConfig>;

// ────────────────────────────────────────────────────────────────────────────
// Grid static-properties (`data.stat-list`) source data — MR-07 step 2.
//
// The 12 `GridElementType` values (the schema/`grid-element-config` axis) do not
// line up 1:1 with the 22 grid keys in `ENTITY_CONFIG` (the registry axis): the
// registry splits lines by phase count, breaks overcurrent devices into
// recloser/fuse/breaker, etc. `GRID_STAT_LIST` is keyed by the coarse element
// type and transcribed (serializably) from `grid-element-config`'s
// `metadataFields`; `GRID_ELEMENT_TYPE_BY_ENTITY` records the 22→12 mapping that
// resolves a registry key to its element type (MR-07 step 6).
//
// Transcription rules (MR-07):
//   • `showWhen` predicates are dropped — a stat-list row is data only.
//   • ABSENT / PII fields are dropped (see `ABSENT_GRID_FIELDS`): consumer
//     `customerName` (zeroed in prod), and we never emit `energized`
//     (no flag) or feeder `gs_max_continuous_current` (0% populated).
//   • Only nameplate/GIS *properties* live here. Live "now" numbers (transformer
//     loading, SCADA metrics) are `fields.headlineMetrics` and remain
//     design-gated — omitted from every grid entry in this MR.
// ────────────────────────────────────────────────────────────────────────────

/** The coarse element-type axis (mirrors `@texturehq/grid-element-config`'s
 *  `GridElementType`; kept in sync by transcription). */
export type GridElementSourceType =
  | "CAPACITOR"
  | "CONSUMER"
  | "DEVICE_LEADER"
  | "GENERATOR"
  | "MOTOR"
  | "NODE"
  | "OVERCURRENT_DEVICE"
  | "OVERHEAD_LINE"
  | "REGULATOR"
  | "SOURCE"
  | "SUPPORT_STRUCTURE"
  | "SWITCH"
  | "TRANSFORMER"
  | "TRANSFORMER_BANK"
  | "UNDERGROUND_LINE";

/**
 * JSONB keys that are ABSENT or PII-zeroed in prod (MR-07 coverage table). No
 * grid stat-list row (or headline metric) may reference one of these — enforced
 * by the conformance test, not just documented.
 */
export const ABSENT_GRID_FIELDS = ["energized", "customerName", "gs_max_continuous_current"] as const;

/**
 * MR-G12 §5 -- voltage stat-list rows still sourced from a RAW metadata key whose
 * unit has NOT been measured against prod.
 *
 * The conformance rule (`entities.test.ts`): a `GRID_STAT_LIST` row with
 * `format.type === "voltage"` must EITHER reference a normalized/decoded key
 * (suffix `Kv`, i.e. the unit is established by construction) OR be listed here.
 * `formatGridStatValue` takes the unit straight from this config and performs no
 * conversion, so an unmeasured raw key is a three-order-of-magnitude error waiting
 * to ship -- exactly how `TRANSFORMER.primaryRatedVoltage` shipped "7 V" for a
 * 7.2 kV transformer. Listing a row here is a deliberate, reviewable admission
 * that its unit is unverified, not an oversight.
 *
 * Status per row (as of 2026-08-24 -- the escape hatch worked, and emptied itself):
 *   • CAPACITOR `nominalVoltage` -- MEASURED (DQ-5). 87/114 populated, all in volts, but 23 are `0`
 *     placeholders that rendered "0 V". Re-sourced from the normalized `nominalVoltageV` extra.
 *   • NODE `nominalVoltage` -- MEASURED (DQ-5): 0% across all four spellings on all 492 Nodes. Row
 *     deleted in MR-P01-5a, together with the SUPPORT_STRUCTURE split that stopped 58,158 poles from
 *     sharing this (now empty) list.
 *   • REGULATOR `outputVoltagePh{A,B,C}` -- MEASURED (2026-08-24) and WRONG, not merely unverified:
 *     the values are a per-unit multiplier (~1.025), so all three rendered "1 V". DQ-7 then found
 *     they duplicate `forward_set_voltage` (123.5 V on a 120 V base). Rows deleted.
 *   • GENERATOR `outputVoltage` -- MEASURED: 0% on all 2,905. Row deleted (and DQ-8 showed the type
 *     is service points, not generation, so there was never a voltage to find).
 *   • MOTOR `voltage` -- still unmeasured, and unmeasurable: element type 11 has ZERO rows in prod.
 *     The only surviving entry, kept because the row still exists (deleting dead registry keys is P02).
 *
 * Entries are `${GridElementSourceType}.${key}`.
 */
export const UNVERIFIED_VOLTAGE_UNIT_ROWS = ["MOTOR.voltage"] as const;

/**
 * Static property rows per `GridElementType`, transcribed from
 * `grid-element-config/src/configs/*.config.ts` `metadataFields`. Serializable:
 * no `showWhen`, no functions. Referenced by grid entities' `properties.statList`.
 */
/**
 * MR-P01-8 -- the capacitor kvar rows, in the two shapes VEC records them (ALL-2565).
 *
 * A polyphase bank's rating is recorded per phase; a single-phase unit's as one total. Prod
 * 2026-08-24: `total_kvar` 76.3%, `kvar_a/b/c` 56.1 / 54.4 / 57.9% -- some capacitors carry one,
 * some the other, some both. Legacy expresses this with a `when` predicate per field
 * (`GridElementTooltip.FIELD_ORDER.CAPACITOR`).
 *
 * `GridStatField` has no `showWhen`, deliberately (see its docstring: a stat-list row is data only,
 * and per-instance visibility is a render-time concern). So the PREDICATE lives in the host
 * (`gridStatFieldsFor`) while the row DECLARATIONS stay here, where every other row lives -- the
 * registry ban is on predicates, not on declaring more than one set.
 *
 * The legacy fallback key `kvar` measured 0% and is not carried: a dead fallback reads as coverage
 * the data does not have.
 */
export const GRID_STAT_LIST_CAPACITOR_TOTAL_KVAR: readonly GridStatField[] = [
  { key: "total_kvar", label: "Rated kvar", format: { type: "power", unit: "kvar", decimals: 1 } },
];

/** Per-phase kvar rows -- polyphase banks, and any unit carrying no total (see `gridStatFieldsFor`). */
export const GRID_STAT_LIST_CAPACITOR_PER_PHASE_KVAR: readonly GridStatField[] = [
  { key: "kvar_a", label: "kvar A", format: { type: "power", unit: "kvar", decimals: 1 } },
  { key: "kvar_b", label: "kvar B", format: { type: "power", unit: "kvar", decimals: 1 } },
  { key: "kvar_c", label: "kvar C", format: { type: "power", unit: "kvar", decimals: 1 } },
];

export const GRID_STAT_LIST = {
  // transformer.config.ts. kVA rating is the decoded nameplate `capacityKva`
  // (= decodeRatedKva(GridElement.ratedKva); ~98% populated, db-findings B2),
  // read from the element's decoded top-level field -- NOT the sparse per-phase
  // `tranKva*` (tranKvaA 0.11% / tranKvaC 39.69%) and NOT the raw `ratedKva`
  // (IEEE-denormal corruption). ABSENT `energized` state omitted.
  // Both voltage rows source NORMALIZED kV extras, not raw metadata keys -- see
  // MR-G12 and `apps/dashboard/lib/entity-pages/defaults/gridVoltage.ts`.
  TRANSFORMER: [
    { key: "capacityKva", label: "kVA Rating", format: { type: "power", unit: "kVA", decimals: 0 } },
    // MR-P01-1 -- legacy shows a Phase row on every type below (GridElementTooltip FIELD_ORDER).
    // Prod 2026-08-24: 93.6% populated on overhead lines, 99.8% underground, 99.2% regulators,
    // 98.5% overcurrent, 93.0% capacitors, 54.3% switches (the rest are STD-only and carry no
    // `phase` at all -- they get no row, which is the point of the empty-value drop). Deliberately
    // NO `format`: the value is a letter set ("ABC", "C") and text-passthrough reproduces legacy
    // byte for byte -- `formatGridStatValue` takes the same passthrough branch for `type: "text"` as
    // for a missing `format`, and being explicit matches the six other text rows in this object AND
    // keeps `GRID_STAT_LIST`'s `as const` union homogeneous (a row without `format` breaks every
    // `f.format?.type` read against the literal type). SOURCE / CONSUMER / NODE / MOTOR are excluded
    // -- see the MR spec.
    { key: "phase", label: "Phase", format: { type: "text" } },
    // MR-G12 -- source is the NORMALIZED `primaryVoltageKv`, not the raw
    // `primaryRatedVoltage`. Prod measurement 2026-08-17, workspace
    // cm932a8ny0009dyf4y12f9jlp, 25,414 transformers: 25,060 = 98.6% populated,
    // median 7.2 kV, 66 rows recorded in volts (7200 x65, 19920 x1), 107 zeros
    // (DATA-ANSWERS-2026-08-17.md §DQ-3). The raw key rendered 7.2 kV as "7 V"
    // and 26.558 kV as "27 V" -- a three-order-of-magnitude error -- because
    // `formatGridStatValue` takes the unit from THIS config and never converts.
    // decimals: 1 is required -- at 0 the distinct 12.47 and 14.43 kV classes
    // collapse into "12" and "14".
    { key: "primaryVoltageKv", label: "Primary Voltage", format: { type: "voltage", unit: "kV", decimals: 1 } },
    // MR-G12 / DQ-7 -- row RESTORED. The 2026-08-09 audit measured the key
    // `ratedOutputVoltage` and got 0%, but a DIFFERENT key is populated:
    // `gs_rated_output_voltage` -- 24,781/25,414 = 97.51%, median 0.12 kV (=120 V)
    // (DATA-ANSWERS-ROUND-2-2026-08-17.md §DQ-7). Sourced via the normalized
    // `outputVoltageKv` extra (GeoJSON preferred over
    // `elementData.ratedTertiaryOutputVoltage`, 97.31%).
    // decimals: 2 -- the secondary lives in 0.12...0.48, where one decimal would
    // squash 0.12 / 0.277 / 0.48 into "0.1" / "0.3" / "0.5".
    { key: "outputVoltageKv", label: "Output Voltage", format: { type: "voltage", unit: "kV", decimals: 2 } },
    // MR-P01-4 -- prod 2026-08-24. NB the raw `rated_kva` (98.0%) is deliberately NOT a row: DQ-1
    // measured it against the decoded `capacityKva` above and got 100.0% EXACT agreement on all
    // 24,903 transformers carrying both, with zero one-sided rows. They are one field; a second row
    // would print the same number twice under two labels, and nothing dedupes them (`capacity_kva`
    // and `rated_kva` do not normalize to the same token). Same reason `primaryVoltage`,
    // `secondary_voltage`, `ratedKva`, `kvaRating` and `ratedOutputVoltage` are absent -- all 0%.
    { key: "manufacturer", label: "Manufacturer", format: { type: "text" } },
    { key: "xfmr_conductor_description", label: "Conductor", format: { type: "text" } },
  ],
  // consumer.config.ts. MR-P01-4 -- 1 row -> 5, the largest single content win in P01.
  // `customerName` stays dropped (ABSENT_GRID_FIELDS: PII-zeroed in prod), but the fields VEC
  // actually identifies a service point by ARE populated (prod 2026-08-24): service_address 96.4%,
  // name 96.5%, account_number 96.4%, service_type 48.2%.
  //
  // ⚠️ `service_address` and `name` are PII and `account_number` is PII-adjacent. They are tagged
  // host-side by `decorateGridStatPii` (piiType/piiEntity are NEVER authored here -- `piiEntity` is
  // per-instance, and the datatable-cell-parity rule bars PII context from agent-authorable props).
  // ⚠️ `service_address` may self-suppress: `buildDisplayMetadata.isTitleEcho` drops it once it
  // becomes the element's title (ALL-2562). That is legacy's behaviour too -- do not read around it.
  // 🔴 `rated_kva` is declared by legacy for CONSUMER and measured 0% on all 45,211 -- not ported.
  CONSUMER: [
    { key: "service_address", label: "Service address", format: { type: "text" } },
    { key: "name", label: "Customer", format: { type: "text" } },
    { key: "account_number", label: "Account #", format: { type: "text" } },
    { key: "meterNumber", label: "Meter Number", format: { type: "text" } },
    { key: "service_type", label: "Service type", format: { type: "text" } },
  ],
  // source.config.ts (substations). Voltage sourced from the DECODED `voltageKv`
  // (kV) -- the raw `busVoltage`/`nominalVoltage` metadata keys are unit-unreliable
  // (e.g. "35" is kV, not V). See MR-G2.
  SOURCE: [
    { key: "voltageKv", label: "Nominal Voltage", format: { type: "voltage", unit: "kV", decimals: 1 } },
    // DQ-6: `station_number` (91.1%) is the SAME field as this one -- string-equal after trim on
    // 51/51 and 48/48 rows where two of the three storage locations are populated. The two normalize
    // differently (`station_number` != `substation_number`), so adding it would render one value
    // twice. One field, one row.
    { key: "substationNumber", label: "Substation Number", format: { type: "text" } },
    // MR-P01-4 -- 67.9%. Deliberately UNITLESS: the values (2.5, 6.25, 9.375, 10.5, 14) are
    // MVA-shaped for a substation transformer, but that is inference from the range, not a
    // measurement, and `formatGridStatValue` takes the unit straight from here and never converts.
    // This config has already shipped "7 V" for 7.2 kV and "1 V" for a 1.025 ratio by inferring.
    // DQ-11 confirms the unit; attach `{ type: "power", unit: "MVA", decimals: 2 }` once it does.
    { key: "trf_capacity", label: "Rated Capacity", format: { type: "text" } },
    // 98.2%. D = Delta, W = Wye.
    { key: "connection_code", label: "Connection", format: { type: "text" } },
    // 🔴 NOT rows, all measured 2026-08-24:
    //   `substation_name` / `utility` / `feeder_count` -- 0%, all three. Legacy declares them and
    //     has never rendered one.
    //   `bus_voltage` -- 100% populated but only TWO distinct values (1 x45, 0 x11). It is a coded
    //     flag, not a measurement; a "Bus Voltage" row would state something false with or without
    //     a unit. The real substation bus voltage is SCADA telemetry (P04).
    //   `regulation_code` -- 100% populated and "R" on all 56 sources. A constant tells a reader
    //     nothing about the element in front of them.
  ],
  // switch.config.ts.
  // MR-P01-5 deleted `ratedCurrent` and `normalState` as "both 0%". 🔄 T6 (2026-08-26) showed that
  // zero was an artifact: neither key exists in production under ANY spelling, so the query measured
  // absence of a key name, not absence of data.
  //   - rated current IS carried, as `gs_max_continuous_current` -> `max_continuous_current`,
  //     28.3% of 1,414, median 100 A, real spread (200->113, 100->60, 600->23). Row restored below
  //     (P01 T7). 161 rows hold `0`; that is a placeholder pocket, not a reason to drop the row.
  //   - normal state IS carried, and was never actually missing from the page: `switchIsOpen`
  //     (75.5%) already ships as "Position". `gs_normal_position_a|b|c` is the richer signal if a
  //     second row is ever wanted.
  // ⚠️ Do NOT reach for `gs_abnormal_state`: populated on 6,369 overcurrent devices and the constant
  // `false` on every one of them. It is what inflated "normal state" coverage to 100% in T6's first
  // pass.
  // `map_number` is NOT addable at all: it is a Prisma column (`mapNumber`), never a
  // `displayMetadata` key, and is not exposed on the `GridElement` GraphQL type -- see P10 T5.
  //
  // ⚠️ `switchIsOpen` (75.5%) is a BOOLEAN. Unformatted it renders `String(false)` = "false", and
  // `false` is deliberately kept as a real value, so a closed switch would read `Position: false`.
  // The true/false -> Open/Closed mapping is a render-time transform the registry cannot express;
  // it is applied by the host decorator (`decorateGridStatSwitchPosition`). Never ship this row
  // without that decorator.
  SWITCH: [
    { key: "switchId", label: "Switch ID", format: { type: "text" } },
    { key: "phase", label: "Phase", format: { type: "text" } },
    // `number` and not `current`: `GridStatFieldFormat.type` has no `current` member, and the
    // formatter treats every non-`text` type identically anyway — it is `unit` that does the work.
    // Amps is safe here: the field names itself, and the values (100 / 200 / 600 / 300) are switch
    // continuous ratings.
    {
      key: "max_continuous_current",
      label: "Max Continuous Current",
      format: { type: "number", unit: "A", decimals: 0 },
    },
    { key: "switchIsOpen", label: "Position", format: { type: "text" } },
    { key: "switch_status", label: "Status", format: { type: "text" } },
    { key: "partnerIdentifier", label: "Partner ID", format: { type: "text" } },
  ],
  // capacitor.config.ts.
  // MR-P01-5 -- `unitSizeKvar` DELETED (0%), and the kvar rating now comes from the polyphase branch
  // in `gridStatFieldsFor` (MR-P01-8), which the registry cannot express: `GridStatField` has no
  // `showWhen`, and VEC records a bank's rating per-phase on polyphase units and as a single total
  // on single-phase ones. `control_type` (legacy-declared) and `kvar` are 0% and never added.
  //
  // DQ-5 settled `nominalVoltage`: it lives ONLY in `gs_nominal_voltage` (87/114 = 76.3%), which
  // `createGridMetadataLookup` does find -- `gs_` is stripped to `nominal_voltage` and the camelCase
  // key normalizes to the same token. All values are volts, so the unit is right. But 23 of the 87
  // are `0` placeholders and `resolveGridStatList` keeps `0`, so those rendered "0 V". Now sourced
  // through the normalized `nominalVoltageV` extra, which drops non-positive readings the way
  // `normalizeVoltageKv` does for transformers.
  CAPACITOR: [
    { key: "phase", label: "Phase", format: { type: "text" } },
    { key: "nominalVoltageV", label: "Nominal Voltage", format: { type: "voltage", unit: "V", decimals: 0 } },
  ],
  // overcurrent-device.config.ts (fuses, breakers, reclosers).
  // MR-P01-4 -- `ratedCurrent` and `deviceType` REMOVED: both measured 0% on all 6,462 prod
  // elements, so neither ever rendered for anyone. Deleting a row nobody has seen is not a parity
  // regression. `rated_amps` (legacy-declared) is 3/6,462 = 0.05% and `gs_max_continuous_current`
  // is 0% -- neither is sourced, and together they are why feeder "% loading" is not buildable.
  //
  // The device's most useful line -- the computed "Type" ("Fuse", "Feeder / Recloser") -- is NOT a
  // row here: it is derived from three API fields (`overcurrentHardwareType`,
  // `overcurrentDeviceSubtype`, `isFeederBay`) rather than a metadata key, so the host prepends it
  // via `overcurrentTypeLabel` (MR-P01-3). `overcurrent_device_subtype` is 98.5% populated but
  // feeds that label, never a row of its own.
  OVERCURRENT_DEVICE: [
    { key: "size_value", label: "Size", format: { type: "text" } },
    { key: "phase", label: "Phase", format: { type: "text" } },
  ],
  // regulator.config.ts.
  // MR-P01-2b -- `outputVoltagePh{A,B,C}` DELETED. They were declared `{ voltage, "V", decimals: 0 }`,
  // but the measured values are ~1.025 (max 1.029167): a PER-UNIT multiplier, not a voltage. Since
  // `formatGridStatValue` takes the unit from this config and never converts, all three rendered the
  // string "1 V" on the ~48% of regulators that carry them. No normalizer can fix that -- the
  // quantity is not a voltage.
  //
  // DQ-7 then showed they are redundant as well as wrong: `forward_set_voltage` (median 123.5,
  // 68.8%) is the SAME set-point expressed on a 120 V base -- 1.029 x 120 = 123.5, confirmed row by
  // row on the 28 regulators carrying both. Keeping both would put one physical quantity on the page
  // twice in two units; the volts form is better populated and does not need converting in the
  // reader's head. (⚠️ DQ-10 checks whether the three phases ever disagree within one element. If
  // they do, a single scalar cannot represent them and these come back as unitless ratios.)
  //
  // `ratedPower` (0%) and the legacy-declared `step_range` (0%) are likewise gone / never added.
  REGULATOR: [
    { key: "rated_kva", label: "Rated kVA", format: { type: "power", unit: "kVA", decimals: 0 } },
    { key: "phase", label: "Phase", format: { type: "text" } },
    { key: "manufacturer", label: "Manufacturer", format: { type: "text" } },
    { key: "high_side_configuration", label: "High Side", format: { type: "text" } },
    // 68.8%. The unit here IS measured, unlike `trf_capacity`: the distribution is 121...123.5 with
    // 84% at exactly 123.5 -- the textbook forward set-point on a 120 V base (+2.9% boost).
    // decimals: 1 because at 0 the whole distribution collapses to "123" / "122".
    // Sourced through the normalized `forwardSetVoltageV` extra so the two `0` placeholders drop.
    { key: "forwardSetVoltageV", label: "Forward Set Voltage", format: { type: "voltage", unit: "V", decimals: 1 } },
  ],
  // overhead-line.config.ts.
  // MR-P01-5 / DQ-2 -- the conductor rows below are LIVE (42.8/39.5/42.7%), not dead as an earlier
  // measurement suggested: that round measured `conductorDescA/B/C` (no `Ph`), a spelling that
  // exists in neither source. Two spellings are real, one per source -- `gs_conductor_a/b/c` (geo,
  // 42.4%, what legacy reads) and `conductorDescPhA/B/C` (STD, what we read) -- and they agree on
  // 94.6% of the lines carrying both. ⚠️ They normalize differently, so BOTH survive into
  // `displayMetadata`; the dump (MR-P01-7) suppresses the geo copy via `GRID_METADATA_ALIASES`.
  //
  // A phase-C run shows only its Conductor Phase C row, which is correct and needs no conditional
  // logic: `conductorDescPhA` is populated only on A-bearing lines (99.9% of them), and
  // `resolveGridStatList` already drops absent keys.
  OVERHEAD_LINE: [
    { key: "impedanceLength", label: "Length", format: { type: "distance", unit: "ft", decimals: 2 } },
    { key: "conductorDescPhA", label: "Conductor Phase A", format: { type: "text" } },
    { key: "conductorDescPhB", label: "Conductor Phase B", format: { type: "text" } },
    { key: "conductorDescPhC", label: "Conductor Phase C", format: { type: "text" } },
    // 67.8% -- higher than any single phase because neutral is recorded across all phases while a
    // phase conductor is recorded only on lines carrying that phase. Overhead only: on underground
    // runs this key is 0.2%.
    { key: "conductorDescNeutral", label: "Conductor Neutral", format: { type: "text" } },
    { key: "phase", label: "Phase", format: { type: "text" } },
    { key: "subtype_cd", label: "Subtype", format: { type: "text" } },
  ],
  // underground-line.config.ts.
  UNDERGROUND_LINE: [
    { key: "impedanceLength", label: "Length", format: { type: "distance", unit: "ft", decimals: 2 } },
    { key: "conductorDescPhA", label: "Conductor Phase A", format: { type: "text" } },
    { key: "conductorDescPhB", label: "Conductor Phase B", format: { type: "text" } },
    { key: "conductorDescPhC", label: "Conductor Phase C", format: { type: "text" } },
    { key: "phase", label: "Phase", format: { type: "text" } },
    // 99.9%. NB no `conductorDescNeutral` row here -- 0.2% on underground, against 67.8% overhead.
    { key: "subtype_cd", label: "Subtype", format: { type: "text" } },
  ],
  // node.config.ts -- junctions and relays only, now that support structures have their own key.
  // MR-P01-5a: the single `nominalVoltage` row is DELETED. DQ-5 measured all four spellings
  // (`nominalVoltage` / `nominal_voltage`, in either source) at 0% on all 492 prod Nodes -- and their
  // `displayMetadata` is entirely empty, both `geoJsonMetadata` and `elementData`. There is nothing
  // to show, so this list is empty and the page renders the Properties section's `state: "empty"`
  // rather than a frame containing one row that never resolves. That IS the truthful render, and it
  // is only reachable now: while `supportStructure` still mapped here, an empty list would have
  // stripped 58,158 poles of their declared rows.
  NODE: [],
  // MR-P01-5a -- SupportStructure (element type 14) is the SECOND-LARGEST type in prod at 58,158
  // elements, and it had no stat list of its own: `GRID_ELEMENT_TYPE_BY_ENTITY.supportStructure`
  // pointed at NODE, whose one row resolved for nobody. So the largest empty Properties section in
  // the product was empty for a mapping reason, not a data reason -- DQ-3 found the type richly
  // populated.
  //
  // These are legacy's five keys in legacy's order with legacy's labels (`species` -> "Material"),
  // transcribed from `GridElementTooltip.FIELD_ORDER.SUPPORT_STRUCTURE`. Coverage 2026-08-24:
  // substation 97.4%, height 91.4%, class 87.8% (pole class codes 4/5/3), species 73.9%
  // (SP = Southern Pine, 39,255), phase 13.1%.
  //
  // ⚖️ DQ-3 also found `owner` 99.8%, `town` 99.8%, `maintenance_area` 99.8%, `feeder_number` 96.5%,
  // `transformer` 95.8%, `attaching` 100% and a dozen more. They are deliberately NOT rows: parity is
  // legacy's five, and the rest reaches the reader through the metadata dump (MR-P01-7), which is
  // what the dump is for. Promoting any of them is a product call about what a pole page leads with.
  //
  // `height` is FEET, measured not assumed: numeric on 53,122 of 53,129, min 0 / p10 35 / median 40 /
  // p90 45 / max 95 -- standard US distribution-pole heights are 35/40/45 ft (40 m would be 131 ft).
  // ⚠️ 7 values are non-numeric; `formatGridStatValue` falls back to `String(raw)` for those, which
  // silently drops the unit. Accepted -- inventing a unit for an unparseable value is worse -- and
  // pinned by a test so the behaviour stays chosen rather than discovered.
  SUPPORT_STRUCTURE: [
    { key: "height", label: "Height", format: { type: "distance", unit: "ft", decimals: 0 } },
    { key: "class", label: "Class", format: { type: "text" } },
    { key: "species", label: "Material", format: { type: "text" } },
    { key: "phase", label: "Phase", format: { type: "text" } },
    { key: "substation", label: "Substation", format: { type: "text" } },
  ],
  // generator.config.ts -- but DQ-8 established that element type 12 is NOT generation. Its populated
  // fields are `service_current_rating` (200 A on every element), `service_number`, `meter_position`,
  // `account_number`, `customer_number` and the customer's name and phones: a metered SERVICE POINT,
  // the same field family as CONSUMER. 100% of the 2,494 linked Texture devices are `type: "meter"`
  // (zero inverters, batteries or PV), the rows come from a WindMil `.std` service-point layer rather
  // than a GeoJSON `Generators` layer, and every generation field -- `rated_kva`, `ratedPower`,
  // `fuel_type`, `outputVoltage`, `nameplate_kw`, `inverter*`, `pv*` -- is 0% on all 2,905.
  //
  // So `ratedPower` and `outputVoltage` are DELETED (both 0%) and nothing generation-shaped replaces
  // them: authoring nameplate rows for an entity the data says is a service connection would be
  // inventing it. Giving these elements the CONSUMER treatment is a product decision, not parity --
  // legacy shows them the Generator field set and therefore shows them almost nothing.
  // 🔴 Their customer PII is why MR-P01-7's dump denylist must be global, not scoped to CONSUMER.
  // 🔄 T6 (2026-08-26) — the "every generation field is 0%" half of DQ-8's argument was a false zero.
  // The measured keys did not exist in prod under any spelling. `gs_generator_system_size` ->
  // `generator_system_size` is populated on 99.1% of all 2,905, median 7.6, with a real distribution
  // (7.6->330, 6->302, 10->199, 5->152, 3.8->125). Until this row landed, a generator page rendered a
  // single field, Phase, while 2,879 of them held a nameplate size.
  //
  // DQ-8's OTHER half still stands and is why the label is deliberately neutral: 100% of the 2,494
  // linked devices are meters, so these elements are service connections. "System Size" reads as the
  // size interconnected AT that point — which is what the numbers look like, residential DER — rather
  // than as generator output. `gs_allocated_kw_A|B|C` (986/919/1,060 rows) is consistent with that.
  //
  // ⚠️ NO `unit`, on purpose. Nothing in the data states one. A median of 7.6 is consistent with kW
  // and nothing more, and this file has just been burned once by an inference that looked as safe.
  // Confirm the unit, then this becomes `{ type: "power", unit: "kW", decimals: 1 }` — a one-line
  // change. Until then the number is on the page, which was the actual gap.
  GENERATOR: [
    { key: "phase", label: "Phase", format: { type: "text" } },
    { key: "generator_system_size", label: "System Size", format: { type: "number", decimals: 1 } },
  ],
  // motor.config.ts.
  MOTOR: [
    { key: "ratedPower", label: "Rated Power", format: { type: "power", unit: "kW", decimals: 1 } },
    { key: "voltage", label: "Voltage", format: { type: "voltage", unit: "V", decimals: 0 } },
  ],
  // Empty for a DATA reason, like `NODE` and unlike the SUPPORT_STRUCTURE mistake above: DQ-3
  // measured every metadata key empty across all 6,571 prod Device Leaders, in both
  // `geoJsonMetadata` and `elementData`. That is what a leader line IS — the location subgraph
  // documents the value as a cartographic pointer, display-only and outside the electrical
  // hierarchy — so there is nothing to curate and never will be. The page renders the Properties
  // section's `state: "empty"`, which is the truthful render.
  DEVICE_LEADER: [],
  // Empty for a MEASUREMENT reason, and the one entry here expected to gain rows. Banks reached prod
  // ES for the first time with the 2026-08-26 VEC load (#14855), and the subgraph documents them as
  // carrying a summed rating and parenting their members — so there IS data to curate, it just has
  // no coverage figure yet, and this file does not author rows against unmeasured keys.
  //
  // Shipping empty costs nothing in the meantime: `resolveGridMetadataDump` renders every metadata
  // key that is not already a curated row, so whatever a bank carries is on the page regardless —
  // only the ordering and the labels are missing. Measure `gs_bank_id` and the rating keys, then
  // transcribe rows the way SUPPORT_STRUCTURE's were.
  TRANSFORMER_BANK: [],
} as const satisfies Record<GridElementSourceType, GridStatField[]>;

export const ENTITY_CONFIG = {
  // ── Platform objects ──────────────────────────────────────────────────
  app: {
    icon: "AppWindow",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "App", plural: "Apps" },
    description: "Workspace tools and third-party integrations",
  },
  site: {
    icon: "MapPin",
    category: "platform",
    archetype: "platform",
    detailPage: true,
    label: { singular: "Site", plural: "Sites" },
    description: "Physical location housing energy systems",
  },
  device: {
    icon: "ShareNetwork",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Device", plural: "Devices" },
    description: "Connected, controllable energy device",
  },
  customer: {
    icon: "Users",
    category: "platform",
    archetype: "platform",
    detailPage: true,
    label: { singular: "Customer", plural: "Customers" },
    description: "End customer or owner of one or more sites",
  },
  contact: {
    icon: "Users",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Contact", plural: "Contacts" },
    description: "Person associated with a customer or site",
  },
  program: {
    icon: "ClipboardText",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Program", plural: "Programs" },
    description: "Demand-response or virtual power plant program",
  },
  workflow: {
    icon: "FlowArrow",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Workflow", plural: "Workflows" },
    description: "Automated rule that runs in response to events",
  },
  collection: {
    icon: "Stack",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Collection", plural: "Collections" },
    description: "Named group of related entities or records",
  },
  board: {
    icon: "TextureBoards",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Board", plural: "Boards" },
    description: "Dashboard for monitoring metrics and devices",
  },
  report: {
    icon: "ChartLine",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Report", plural: "Reports" },
    description: "Saved analytics view or generated report",
  },
  event: {
    icon: "ClockCounterClockwise",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Event", plural: "Events" },
    description: "Recorded system event or logged activity",
  },
  alert: {
    icon: "Warning",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Alert", plural: "Alerts" },
    description: "Notification raised when attention is needed",
  },
  monitor: {
    icon: "TextureMonitors",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Monitor", plural: "Monitors" },
    description: "Rule that watches a metric against a threshold",
  },
  insight: {
    icon: "LightbulbFilament",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Insight", plural: "Insights" },
    description: "Derived finding surfaced from platform data",
  },
  command: {
    icon: "Play",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Command", plural: "Commands" },
    description: "Control action issued to a connected device",
  },

  explore: {
    icon: "TextureExplore",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Explore", plural: "Explore" },
    description: "The BEAM map surface for exploring the connected grid.",
  },
  boards: {
    icon: "TextureBoards",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Boards", plural: "Boards" },
    description: "The BEAM surface for building and viewing grid dashboards.",
  },
  monitors: {
    icon: "TextureMonitors",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Monitors", plural: "Monitors" },
    description: "The BEAM surface for watching conditions across the grid.",
  },
  askTexture: {
    icon: "Sparkle",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Ask Texture", plural: "Ask Texture" },
    description: "The BEAM conversational surface for investigating grid data and creating views and monitors.",
  },

  // Branded products are display metadata, not routed asset records.
  survey: {
    icon: "TextureSurvey",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Survey", plural: "Survey" },
    description: "The onboarding engagement that maps a utility's systems to the Terrain schema.",
  },
  seismic: {
    icon: "TextureSeismic",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Seismic", plural: "Seismic" },
    description: "AI-powered interpretation of raw data, automatically mapped into the grid model.",
  },
  waypoint: {
    icon: "TextureWaypoint",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Waypoint", plural: "Waypoint" },
    description: "Flexible customer data collection and authorization for consumer devices.",
  },
  terrain: {
    icon: "TextureTerrain",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Terrain", plural: "Terrain" },
    description: "The shared schema for grid assets, locations, and relationships. An AI-ready data model standard.",
  },
  magma: {
    icon: "TextureMagma",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Magma", plural: "Magma" },
    description:
      "The execution engine for streaming, monitoring, automation, insights, and simulations, using data in the Terrain schema.",
  },
  ground: {
    icon: "TextureGround",
    category: "platform",
    archetype: "platform",
    detailPage: false,
    label: { singular: "Ground", plural: "Ground" },
    description:
      "On-premises hardware Stations for local control, AI processing, and continuity when cloud connectivity drops.",
  },

  // ── Grid elements ─────────────────────────────────────────────────────
  // Superset of grid-infrastructure element types. The 12 types rendered in
  // the dashboard Explore experience (GRID_ELEMENT_TYPES in
  // apps/dashboard/lib/map-utils.ts) are ALL represented here with matching
  // names; this list additionally carries data-model types that aren't
  // first-class Explore layers (substation, meter, motor switch) and the
  // overcurrent sub-classes (recloser, fuse, breaker).
  //
  // ── Reachability (MR-P02-3, re-verified 2026-08-25) ──────────────────
  // Eight of these keys cannot be reached at runtime, and the cause is the
  // composed schema, not this file. `gridEntityTypeFor`
  // (apps/dashboard/lib/entity-pages/defaults/gridPageModel.ts) turns a
  // `GridElementType` into one of these keys, and the gateway only ever emits
  // 13 of the 18 values the device subgraph declares: RECLOSER, FUSE,
  // SECTIONALIZER, BREAKER and FEEDER_BAY are `@inaccessible`
  // (infra/supergraph/schemas/device.graphql), and the location
  // subgraph never declared them at all. Upstream of that, `mapElementTypeToEnum`
  // (domains/location/src/services/gridElement/utils.ts) knows exactly 13 element
  // type names. So a key reachable only from an @inaccessible value, or only from
  // a distinction the enum does not draw (phase count), is dead by construction.
  // Each one says which below, and where its elements actually resolve.
  // `motorSwitch` is dead for a different reason and says so.
  //
  // ⚠️ The dashboard's generated types disagree, and they are the ones that are
  // wrong: `apps/dashboard/codegen.ts` points `schema` at a glob over individual
  // subgraph SDL rather than at the composed supergraph, and merging SDL does not
  // apply `@inaccessible` — so `lib/@generated/graphql.ts` declares all 18 values
  // as if they were live. Do NOT "fix" a comment here to agree with it. Tracked as
  // T1 in implementation/p02-hygiene/TICKETS-followups.md.
  //
  // 🧊 No key is deleted here. They are pinned by entities.test.ts
  // (`toHaveLength(30)`, the archetype histogram, `toHaveLength(22)`) and those
  // same numbers are headline figures in DESIGN-REFERENCE.md — removing one is a
  // coordinated registry + test + design-doc edit for zero user-visible effect.
  source: {
    icon: "TextureSource",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.SOURCE },
    label: { singular: "Source", plural: "Sources" },
    description: "Upstream feed where the network originates",
  },
  substation: {
    icon: "TextureSource",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.SOURCE },
    label: { singular: "Substation", plural: "Substations" },
    description: "Facility that steps down transmission voltage",
    // Dead by construction: there is no SUBSTATION value in the enum. `SOURCE` IS
    // the substation — the location subgraph says so in its own doc-comment on the
    // value ("Sources represent substations and will always be at the top of the
    // hierarchy"). Every substation element therefore resolves to `source`, which
    // carries this same GRID_STAT_LIST.SOURCE. This key is a label-only alias that
    // nothing selects.
  },
  capacitor: {
    icon: "TextureCapacitor",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.CAPACITOR },
    label: { singular: "Capacitor", plural: "Capacitors" },
    description: "Capacitor bank for power-factor and voltage support",
  },
  switch: {
    icon: "TextureSwitchClosed",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.SWITCH },
    label: { singular: "Switch", plural: "Switches" },
    description: "Sectionalizing or disconnect switch on the line",
    states: {
      open: { icon: "TextureSwitch", color: "open" },
      closed: { icon: "TextureSwitchClosed", color: "closed" },
    },
  },
  sectionalizer: {
    icon: "TextureSectionalizerClosed",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.SWITCH },
    label: { singular: "Sectionalizer", plural: "Sectionalizers" },
    description: "Counts upstream recloser trips and opens to isolate a faulted section",
    // Dead by construction: `SECTIONALIZER` is @inaccessible, so the gateway never
    // emits it and `gridEntityTypeFor`'s SECTIONALIZER branch never fires. Nothing
    // resolves to this key. Its stat list was transcribed from GRID_STAT_LIST.SWITCH,
    // which is what an element reported as `SWITCH` renders through `switch` instead;
    // what this key would have added is the sectionalizer icon pair and the
    // trip-count description, not data.
    states: {
      open: { icon: "TextureSectionalizerOpen", color: "open" },
      closed: { icon: "TextureSectionalizerClosed", color: "closed" },
    },
  },
  relay: {
    icon: "Cpu",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.NODE },
    label: { singular: "Relay", plural: "Relays" },
    description: "Microprocessor relay that senses faults and trips devices",
  },
  transformer: {
    icon: "TextureTransformer",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.TRANSFORMER },
    label: { singular: "Transformer", plural: "Transformers" },
    description: "Transformer that steps voltage up or down",
  },
  meter: {
    icon: "Gauge",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.CONSUMER },
    label: { singular: "Meter", plural: "Meters" },
    description: "Revenue meter at the point of consumption",
    // MR-B3 reconciliation (see m1-meter/meter-consumer-routing-findings.md): the
    // meter's live Now (power / consumption / voltage / current from MeterState)
    // renders on the DEVICE page via @texturehq/device-config `meter.config.ts`,
    // keyed by deviceId. The grid pipeline can't hydrate device state from a gsGuid
    // (it resolves only `kind:"scada"` telemetry), and no grid element ever routes
    // to the `meter` entity — a meter's grid node is a Consumer
    // (`gridEntityTypeFor("CONSUMER") -> "consumer"`). So this grid entry is
    // properties-only like `consumer`: no `fields.headlineMetrics`, no `fusedMetricId`,
    // so it never declares a Now it can't feed (`entityShowsNow("meter") === false`).
  },
  consumer: {
    icon: "Gauge",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.CONSUMER },
    label: { singular: "Consumer", plural: "Consumers" },
    description: "Service point or customer premise on the grid",
  },
  singlePhaseOverheadLine: {
    icon: "TextureSinglePhaseOverheadLine",
    category: "grid",
    archetype: "grid-line",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.OVERHEAD_LINE },
    label: { singular: "Single-Phase Overhead Line", plural: "Single-Phase Overhead Lines" },
    description: "Overhead line carrying one phase conductor",
    // Dead by construction: `OVERHEAD_LINE` draws no phase-count distinction, so
    // `gridEntityTypeFor` resolves EVERY overhead line to `threePhaseOverheadLine`
    // regardless of how many conductors it carries. All three overhead keys share
    // GRID_STAT_LIST.OVERHEAD_LINE, so only the label and icon are lost — and
    // nothing selects them. Phase count lives in the `phase` property row.
  },
  twoPhaseOverheadLine: {
    icon: "TextureTwoPhaseOverheadLine",
    category: "grid",
    archetype: "grid-line",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.OVERHEAD_LINE },
    label: { singular: "Two-Phase Overhead Line", plural: "Two-Phase Overhead Lines" },
    description: "Overhead line carrying two phase conductors",
    // Dead by construction, same as `singlePhaseOverheadLine`: `OVERHEAD_LINE`
    // always resolves to `threePhaseOverheadLine`.
  },
  threePhaseOverheadLine: {
    icon: "TextureThreePhaseOverheadLine",
    category: "grid",
    archetype: "grid-line",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.OVERHEAD_LINE },
    label: { singular: "Three-Phase Overhead Line", plural: "Three-Phase Overhead Lines" },
    description: "Overhead line carrying three phase conductors",
  },
  singlePhaseUndergroundLine: {
    icon: "TextureSinglePhaseUndergroundLine",
    category: "grid",
    archetype: "grid-line",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.UNDERGROUND_LINE },
    label: { singular: "Single-Phase Underground Line", plural: "Single-Phase Underground Lines" },
    description: "Buried line carrying one phase conductor",
    // Dead by construction: `UNDERGROUND_LINE` draws no phase-count distinction, so
    // `gridEntityTypeFor` resolves EVERY underground line to
    // `threePhaseUndergroundLine`. All three underground keys share
    // GRID_STAT_LIST.UNDERGROUND_LINE; phase count lives in the `phase` property row.
  },
  twoPhaseUndergroundLine: {
    icon: "TextureTwoPhaseUndergroundLine",
    category: "grid",
    archetype: "grid-line",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.UNDERGROUND_LINE },
    label: { singular: "Two-Phase Underground Line", plural: "Two-Phase Underground Lines" },
    description: "Buried line carrying two phase conductors",
    // Dead by construction, same as `singlePhaseUndergroundLine`: `UNDERGROUND_LINE`
    // always resolves to `threePhaseUndergroundLine`.
  },
  threePhaseUndergroundLine: {
    icon: "TextureThreePhaseUndergroundLine",
    category: "grid",
    archetype: "grid-line",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.UNDERGROUND_LINE },
    label: { singular: "Three-Phase Underground Line", plural: "Three-Phase Underground Lines" },
    description: "Buried line carrying three phase conductors",
  },
  motorSwitch: {
    icon: "Diamond",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.MOTOR },
    label: { singular: "Motor Switch", plural: "Motor Switches" },
    description: "Motor-operated switch or motor load element",
    // Dead in PRACTICE, not by construction — and the difference is why this comment
    // is separate from the eight above. `MOTOR` is an accessible enum value and
    // `mapElementTypeToEnum` knows the name "Motor", so `gridEntityTypeFor("MOTOR")`
    // reaches this key normally. There is simply nothing to reach it with: element
    // type 11 has ZERO rows in production across all three grid tenants (re-measured
    // 2026-08-25 over 500,469 elements). If it ever acquires data the fix here is
    // "nothing"; the fix for the eight unreachable keys is "delete the key".
    //
    // 🔴 The neighbouring case needs no key at all: element type 15, "Transformer
    // Bank", is not in the enum and not in `mapElementTypeToEnum`'s 13 names — a type
    // with no schema rather than a type with no rows. If it ever acquires elements
    // the mapper THROWS, exactly as it does today for "Device Leader"
    // (implementation/p01-grid-content/TICKETS-followups.md T1, Sentry
    // DOMAINS-LOCATION-3A, 6,571 elements). Adding a registry key would not prevent
    // that; teaching the mapper the name would.
  },
  recloser: {
    icon: "TextureRecloser",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.OVERCURRENT_DEVICE },
    label: { singular: "Recloser", plural: "Reclosers" },
    description: "Auto-reclosing device that clears transient faults",
    states: {
      open: { color: "open" },
      closed: { color: "closed" },
    },
  },
  fuse: {
    icon: "TextureFuse",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.OVERCURRENT_DEVICE },
    label: { singular: "Fuse", plural: "Fuses" },
    description: "Fusible element providing overcurrent protection",
    // Dead by construction: `FUSE` is @inaccessible, so its branch in
    // `gridEntityTypeFor` never fires. The whole family reaches the gateway as
    // `OVERCURRENT_DEVICE`, which falls to that switch's `default:` and resolves to
    // `recloser` — carrying the identical GRID_STAT_LIST.OVERCURRENT_DEVICE rows.
    // The fuse/breaker/recloser distinction a reader expects from this key is
    // rendered instead as a computed **Type** row off `overcurrentDeviceSubtype`
    // (apps/dashboard/lib/entity-pages/defaults/gridMetadataDump.ts).
  },
  breaker: {
    icon: "TextureBreaker",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.OVERCURRENT_DEVICE },
    label: { singular: "Breaker", plural: "Breakers" },
    description: "Circuit breaker that interrupts fault current",
    // Dead by construction, same as `fuse`: `BREAKER` is @inaccessible and the family
    // arrives as `OVERCURRENT_DEVICE`, resolving to `recloser`. The `states` below are
    // therefore unreachable too — kept because they are the correct mapping if a path
    // ever bypasses the gateway.
    states: {
      open: { color: "open" },
      closed: { color: "closed" },
    },
  },
  generator: {
    icon: "Wind",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.GENERATOR },
    label: { singular: "Generator", plural: "Generators" },
    description: "Distributed generation unit feeding the grid",
  },
  regulator: {
    icon: "TextureRegulator",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.REGULATOR },
    label: { singular: "Regulator", plural: "Regulators" },
    description: "Voltage regulator that holds line voltage in range",
  },
  supportStructure: {
    icon: "TextureSupportStructure",
    category: "grid",
    archetype: "grid-line",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.SUPPORT_STRUCTURE },
    label: { singular: "Support Structure", plural: "Support Structures" },
    description: "Structure carrying overhead line equipment",
  },
  // `grid-line`, not `grid-energized`: a leader line is a map annotation that the location subgraph
  // documents as "display-only; never part of the electrical hierarchy". `detailPage` is still true
  // for a narrower reason than reachability — `gridEntityTypeFor` is TOTAL over the element-type
  // union, so refusing an entry withholds no page; it just selects `recloser` for one.
  deviceLeader: {
    icon: "FlowArrow",
    category: "grid",
    archetype: "grid-line",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.DEVICE_LEADER },
    label: { singular: "Device Leader", plural: "Device Leaders" },
    description: "Cartographic leader line pointing an offset device symbol to its true location",
  },
  transformerBank: {
    icon: "TextureTransformer",
    category: "grid",
    archetype: "grid-energized",
    detailPage: true,
    properties: { statList: GRID_STAT_LIST.TRANSFORMER_BANK },
    label: { singular: "Transformer Bank", plural: "Transformer Banks" },
    description: "Grouping of single-phase transformer units sharing a bank id",
  },

  // ── Devices ───────────────────────────────────────────────────────────
  // Maps to @texturehq/device DeviceTypeEnum (behind-the-meter assets).
  battery: {
    icon: "BatteryHigh",
    category: "device",
    archetype: "device",
    detailPage: true,
    label: { singular: "Battery", plural: "Batteries" },
    description: "Behind-the-meter battery or storage system",
    fusedMetricId: "soc",
    // Lifted (serializably) from @texturehq/device-config battery.config.ts.
    fields: {
      headlineMetrics: [
        {
          id: "power",
          label: "device.battery.fields.gridPower",
          source: { kind: "state", path: "state.gridPower" },
          format: { type: "power", decimals: 1 },
          icon: "Lightning",
        },
        {
          id: "soc",
          label: "device.battery.fields.soc",
          source: { kind: "state", path: "state.chargePercentage" },
          format: { type: "percentage", decimals: 0 },
          icon: "BatteryCharging",
        },
        {
          id: "batteryPower",
          label: "device.battery.fields.batteryPower",
          source: { kind: "state", path: "state.chargeRate" },
          format: { type: "power", decimals: 1 },
          icon: "TrendUp",
        },
        {
          id: "backupReserve",
          label: "device.battery.fields.backupReserve",
          source: { kind: "state", path: "state.backupReserve" },
          format: { type: "percentage", decimals: 0 },
          icon: "BatteryCharging",
        },
      ],
    },
    stateRules: {
      primaryState: {
        label: "device.battery.fields.status",
        fieldPath: "state.chargingState",
        mapping: {
          charging: "charging",
          discharging: "discharging",
          idle: "idle",
          unknown: "unknown",
        },
        fallback: "unknown",
      },
      secondaryState: {
        label: "device.battery.fields.gridState",
        fieldPath: "state.gridStatus",
        mapping: {
          importing: "importing",
          exporting: "exporting",
          idle: "idle",
          unknown: "unknown",
        },
        fallback: "unknown",
      },
    },
  },
  evCharger: {
    icon: "ChargingStation",
    category: "device",
    archetype: "device",
    detailPage: true,
    label: { singular: "EV Charger", plural: "EV Chargers" },
    description: "Electric-vehicle charging station or port",
    fusedMetricId: "power",
    // Lifted (serializably) from @texturehq/device-config ev-charger.config.ts.
    fields: {
      headlineMetrics: [
        {
          id: "power",
          label: "device.evCharger.fields.power",
          source: { kind: "state", path: "state.chargerWattage" },
          format: { type: "power", decimals: 1 },
          icon: "Lightning",
        },
        {
          id: "isPluggedIn",
          label: "device.evCharger.fields.isPluggedIn",
          source: { kind: "state", path: "state.isPluggedIn" },
          format: { type: "boolean", trueText: "Yes", falseText: "No" },
          icon: "Plug",
        },
        {
          id: "voltage",
          label: "device.evCharger.fields.voltage",
          source: { kind: "state", path: "state.chargerVoltage" },
          format: { type: "voltage", decimals: 1 },
          icon: "Lightning",
        },
        {
          id: "current",
          label: "device.evCharger.fields.current",
          source: { kind: "state", path: "state.chargerCurrent" },
          format: { type: "current", decimals: 1 },
          icon: "Lightning",
        },
      ],
    },
    stateRules: {
      primaryState: {
        label: "device.evCharger.fields.status",
        fieldPath: "state.chargingState",
        mapping: {
          charging: "charging",
          discharging: "discharging",
          idle: "idle",
          unknown: "unknown",
        },
        fallback: "unknown",
      },
    },
  },
  inverter: {
    icon: "Sun",
    category: "device",
    archetype: "device",
    detailPage: true,
    label: { singular: "Solar / Inverter", plural: "Solar / Inverters" },
    description: "Solar PV inverter and its connected array",
    fusedMetricId: "power",
    // Headline metrics lifted (serializably) from
    // @texturehq/device-config inverter.config.ts. State handling: see `stateRules`.
    fields: {
      headlineMetrics: [
        {
          id: "power",
          label: "device.inverter.fields.power",
          source: { kind: "state", path: "state.power" },
          format: { type: "power", decimals: 0 },
          icon: "Lightning",
        },
        {
          id: "gridPower",
          label: "device.inverter.fields.gridPower",
          source: { kind: "state", path: "state.gridPower" },
          format: { type: "power", decimals: 0 },
          icon: "GridFour",
        },
        {
          id: "generatedEnergy",
          label: "device.inverter.fields.generatedEnergy",
          source: { kind: "state", path: "state.generatedEnergy" },
          format: { type: "energy", decimals: 1 },
          icon: "Lightning",
        },
        {
          id: "lifetimeEnergyGenerated",
          label: "device.inverter.fields.lifetimeEnergyGenerated",
          source: { kind: "state", path: "state.lifetimeEnergyGenerated" },
          format: { type: "energy", decimals: 0 },
          icon: "Lightning",
        },
      ],
    },
    // Inverter production state (on/idle) derives from the numeric `state.power`
    // via a resolver in device-config — not a serializable value→token map — so
    // it is not surfaced. Grid flow IS a plain value→token map, so it stands in as
    // the serializable primary chip until `entity.now` resolves production host-side.
    // Keys match the GraphQL `GridStatus` enum, which is lowercase (verified against
    // the device subgraph schema).
    stateRules: {
      primaryState: {
        label: "device.inverter.fields.gridStatus",
        fieldPath: "state.gridStatus",
        mapping: {
          exporting: "exporting",
          importing: "importing",
          idle: "idle",
        },
        fallback: "unknown",
      },
    },
  },
  sensor: {
    icon: "TextureSensor",
    category: "device",
    archetype: "device",
    detailPage: true,
    label: { singular: "Sensor", plural: "Sensors" },
    description: "Connected grid and power-quality sensor",
    // Sensor readings arrive through telemetry, not typed DeviceState fields.
    // The host owns telemetry and connectivity; no state-based metric rail here.
  },
  thermostat: {
    icon: "Thermometer",
    category: "device",
    archetype: "device",
    detailPage: true,
    label: { singular: "Thermostat", plural: "Thermostats" },
    description: "Smart thermostat or connected HVAC control",
    fusedMetricId: "ambientTemperature",
    // Lifted from @texturehq/device-config thermostat.config.ts. That config
    // authors a single headline metric (ambientTemperature), but the design
    // mandates 2–4. `heatTarget`/`coolTarget` — real, serializable metrics that
    // live in the device-config `additionalMetrics` and appear on the thermostat
    // detail mock (current + setpoints) — are promoted to headline to satisfy
    // the range without inventing data.
    fields: {
      headlineMetrics: [
        {
          id: "ambientTemperature",
          label: "device.thermostat.fields.ambientTemperature",
          source: { kind: "state", path: "state.ambientTemperature" },
          format: { type: "temperature", decimals: 0 },
          icon: "Thermometer",
        },
        {
          id: "heatTarget",
          label: "device.thermostat.fields.heatTarget",
          source: { kind: "state", path: "state.heatTarget" },
          format: { type: "temperature", decimals: 0 },
          icon: "Flame",
        },
        {
          id: "coolTarget",
          label: "device.thermostat.fields.coolTarget",
          source: { kind: "state", path: "state.coolTarget" },
          format: { type: "temperature", decimals: 0 },
          icon: "Snowflake",
        },
        {
          id: "fanMode",
          label: "device.thermostat.fields.fanMode",
          source: { kind: "state", path: "state.fanMode" },
          format: { type: "text" },
        },
      ],
    },
    stateRules: {
      primaryState: {
        label: "device.thermostat.fields.operatingMode",
        fieldPath: "state.operatingMode",
        mapping: {
          heat: "heat",
          cool: "cool",
          auto: "auto",
          off: "off",
          eco: "eco",
          unknown: "unknown",
        },
        fallback: "unknown",
      },
    },
  },
  vehicle: {
    icon: "Car",
    category: "device",
    archetype: "device",
    detailPage: true,
    label: { singular: "EV", plural: "EVs" },
    description: "Electric vehicle managed as a flexible load",
    fusedMetricId: "chargePercentage",
    // Lifted (serializably) from @texturehq/device-config vehicle.config.ts.
    fields: {
      headlineMetrics: [
        {
          id: "chargePercentage",
          label: "device.vehicle.fields.chargePercentage",
          source: { kind: "state", path: "state.chargePercentage" },
          format: { type: "percentage", decimals: 0 },
          icon: "BatteryCharging",
        },
        {
          id: "range",
          label: "device.vehicle.fields.range",
          source: { kind: "state", path: "state.range" },
          format: { type: "distance", unit: "mi", decimals: 0 },
          icon: "GasPump",
        },
        {
          id: "chargeLimit",
          label: "device.vehicle.fields.chargeLimit",
          source: { kind: "state", path: "state.chargeLimit" },
          format: { type: "percentage", decimals: 0 },
          icon: "BatteryCharging",
        },
        {
          id: "isPluggedIn",
          label: "device.vehicle.fields.isPluggedIn",
          source: { kind: "state", path: "state.isPluggedIn" },
          format: { type: "boolean", trueText: "Yes", falseText: "No" },
          icon: "Plug",
        },
      ],
    },
    stateRules: {
      primaryState: {
        label: "device.vehicle.fields.status",
        fieldPath: "state.chargingState",
        mapping: {
          charging: "charging",
          discharging: "discharging",
          idle: "idle",
          unknown: "unknown",
        },
        fallback: "unknown",
      },
    },
  },
  waterHeater: {
    icon: "Drop",
    category: "device",
    archetype: "device",
    detailPage: true,
    label: { singular: "Water Heater", plural: "Water Heaters" },
    description: "Connected or smart electric water heater",
    // No @texturehq/device-config entry for water heaters yet, so no headline
    // metrics to lift. `fields` is omitted → `entity.now` declines to render its
    // metric cells until a source config exists. Do not invent metrics.
  },
} as const satisfies Record<string, EntityConfig>;

export type EntityType = keyof typeof ENTITY_CONFIG;

/**
 * Every entity key in the `grid` category, as runtime data.
 *
 * Derived from `ENTITY_CONFIG` rather than written out, so a grid entity added
 * to the registry joins this set with no second edit — the failure mode a
 * hand-maintained list has, and the one that already cost us once: two
 * `GridElementType` values added to a *generated* union compiled cleanly, fell
 * through to a `default:`, and gave 6,571 Device Leaders the wrong icon and
 * label (see `gridEntityTypeFor` in the dashboard).
 *
 * Exists because a grid element's type key is the block/agent boundary where the
 * key is a *value* rather than a TypeScript type: `content.grid-element-link`
 * receives it as JSON from a model, so nothing checks it at compile time and the
 * icon lookup fails silently to a generic glyph. This is what a runtime
 * validator and a prop contract can name.
 */
export const gridEntityTypeKeys: readonly EntityType[] = Object.freeze(
  (Object.keys(ENTITY_CONFIG) as EntityType[]).filter((key) => ENTITY_CONFIG[key].category === "grid")
);

/** Set form of {@link gridEntityTypeKeys}, for O(1) membership checks. */
const GRID_ENTITY_TYPE_KEY_SET: ReadonlySet<string> = new Set<string>(gridEntityTypeKeys);

/**
 * Whether `value` is an entity key in the `grid` category.
 *
 * The guard a caller needs before handing an untrusted string — an agent-authored
 * block prop, a URL segment — to `ENTITY_CONFIG` for a glyph. `ENTITY_CONFIG` is
 * typed as a complete record, so an unknown key type-checks and resolves to
 * `undefined` at runtime; this is what turns that into an answer.
 */
export function isGridEntityTypeKey(value: unknown): value is EntityType {
  return typeof value === "string" && GRID_ENTITY_TYPE_KEY_SET.has(value);
}

/**
 * The 22→12 mapping (MR-07 step 6): every grid registry key resolved to its
 * coarse `GridElementType`. The registry splits some element types by phase
 * count or sub-class (lines → 1P/2P/3P, overcurrent → recloser/fuse/breaker,
 * `NODE` → pole/relay/support structure), so several keys share one element
 * type. This is the recorded source-of-truth for which `GRID_STAT_LIST` a grid
 * entity's `properties.statList` was transcribed from; a conformance test asserts
 * each grid entity's stat-list is exactly `GRID_STAT_LIST[mapping[key]]`.
 */
export const GRID_ELEMENT_TYPE_BY_ENTITY = {
  source: "SOURCE",
  substation: "SOURCE",
  capacitor: "CAPACITOR",
  switch: "SWITCH",
  sectionalizer: "SWITCH",
  relay: "NODE",
  transformer: "TRANSFORMER",
  meter: "CONSUMER",
  consumer: "CONSUMER",
  singlePhaseOverheadLine: "OVERHEAD_LINE",
  twoPhaseOverheadLine: "OVERHEAD_LINE",
  threePhaseOverheadLine: "OVERHEAD_LINE",
  singlePhaseUndergroundLine: "UNDERGROUND_LINE",
  twoPhaseUndergroundLine: "UNDERGROUND_LINE",
  threePhaseUndergroundLine: "UNDERGROUND_LINE",
  motorSwitch: "MOTOR",
  recloser: "OVERCURRENT_DEVICE",
  fuse: "OVERCURRENT_DEVICE",
  breaker: "OVERCURRENT_DEVICE",
  generator: "GENERATOR",
  regulator: "REGULATOR",
  supportStructure: "SUPPORT_STRUCTURE",
  deviceLeader: "DEVICE_LEADER",
  transformerBank: "TRANSFORMER_BANK",
} as const satisfies Partial<Record<EntityType, GridElementSourceType>>;

/**
 * Get entity configuration by type
 *
 * @param entityType - The entity type key
 * @returns Entity configuration object
 *
 * @example
 * ```tsx
 * const config = getEntityConfig("site");
 * // { icon: "MapPin", category: "platform", label: { singular: "Site", plural: "Sites" }, ... }
 * ```
 */
export function getEntityConfig(entityType: EntityType): EntityConfig {
  return ENTITY_CONFIG[entityType];
}

/**
 * Get entity icon name
 *
 * @param entityType - The entity type key
 * @returns Phosphor icon name
 *
 * @example
 * ```tsx
 * const iconName = getEntityIcon("site"); // "MapPin"
 * ```
 */
export function getEntityIcon(entityType: EntityType): IconName {
  return ENTITY_CONFIG[entityType].icon;
}

/**
 * Get entity label (singular or plural)
 *
 * @param entityType - The entity type key
 * @param plural - Whether to return plural form
 * @returns Entity label string
 *
 * @example
 * ```tsx
 * getEntityLabel("site", false); // "Site"
 * getEntityLabel("site", true);  // "Sites"
 * ```
 */
export function getEntityLabel(entityType: EntityType, plural: boolean = false): string {
  return plural ? ENTITY_CONFIG[entityType].label.plural : ENTITY_CONFIG[entityType].label.singular;
}

/**
 * Get the category an entity belongs to.
 *
 * @param entityType - The entity type key
 * @returns The entity's category
 */
export function getEntityCategory(entityType: EntityType): EntityCategory {
  return ENTITY_CONFIG[entityType].category;
}

/**
 * Inclusive bounds on `fields.headlineMetrics`. The design's `entity.now`
 * acceptance criteria say the cell count adapts across this range. Exported so
 * the conformance test enforces the bound rather than documenting it.
 */
export const HEADLINE_METRIC_BOUNDS = { min: 2, max: 4 } as const;

/**
 * Resolve the base page composition for an entity. This is what lets the 30
 * in-scope entities share five compositions instead of 30 pages of page code.
 *
 * @param entityType - The entity type key
 * @returns The entity's archetype
 * @throws If `entityType` is not a known entity (never returns `undefined`, so
 *   a typo surfaces as a loud error rather than a silently missing composition).
 *
 * @example
 * ```tsx
 * archetypeFor("battery");     // "device"
 * archetypeFor("transformer"); // "grid-energized"
 * archetypeFor("nope");        // throws
 * ```
 */
export function archetypeFor(entityType: EntityType): EntityArchetype {
  const config = ENTITY_CONFIG[entityType];
  if (!config) {
    throw new Error(`archetypeFor: unknown entity type "${String(entityType)}"`);
  }
  return config.archetype;
}

/**
 * Whether an entity has a reachable detail page. The registry is the single
 * answer to "does this entity have a page?".
 *
 * @param entityType - The entity type key
 */
export function entityHasDetailPage(entityType: EntityType): boolean {
  return getEntityConfig(entityType).detailPage;
}

/**
 * Whether `entity.now` renders a metric grid for this entity. True iff the
 * entity declares at least one headline metric. Grid elements (no metric
 * registry yet), Site, Customer and Water Heater return `false` — `entity.now`
 * declines to render its cells rather than crashing or rendering empty.
 *
 * Exported deliberately, with no host consumer yet (MR-P02-4). Inside this package
 * it backs the conformance assertions in `entities.test.ts`; outside it, the
 * intended consumer is P11's `visible-when` work, which needs "does this entity
 * have a Now at all?" to decide whether to place the block. Checked rather than
 * guessed: knip (`--workspace packages/edges --include exports`) does not flag it,
 * because `src/index.ts` and `src/server.ts` are configured entry points. If that
 * changes, drop the `export` — it is used internally either way.
 *
 * @param entityType - The entity type key
 */
export function entityShowsNow(entityType: EntityType): boolean {
  return (getEntityConfig(entityType).fields?.headlineMetrics.length ?? 0) > 0;
}

/**
 * The static-property (`data.stat-list`) rows for an entity, or an empty array
 * when it has none. Grid entities carry a non-empty stat-list transcribed from
 * `grid-element-config`; devices and platform objects return `[]`.
 *
 * Note this is independent of `entityShowsNow`: a static stat-list is *not* an
 * `entity.now`. A grid line has `properties.statList` (structural nameplate) yet
 * `entityShowsNow` is `false` — that is the intended MR-07 split.
 *
 * @param entityType - The entity type key
 */
export function getEntityStatList(entityType: EntityType): readonly GridStatField[] {
  return getEntityConfig(entityType).properties?.statList ?? [];
}

/**
 * Whether an entity has a non-empty static-property (`data.stat-list`) block.
 *
 * @param entityType - The entity type key
 */
export function entityHasStatList(entityType: EntityType): boolean {
  return getEntityStatList(entityType).length > 0;
}
